// Present Mind App - Landing Page Mock Data

export const heroData = {
  tagline: "Quality Time with Every Conversation",
  headline: "Focus on Your Client, Not Your Notepad",
  subheadline: "AI-powered meeting assistant for frontline housing staff. Whether you're a support worker, neighbourhood officer, tenancy coach, or income officer - focus on the person in front of you while AI captures every detail.",
  ctaPrimary: "Request a Demo",
  ctaSecondary: "Learn More"
};

export const features = [
  {
    id: 1,
    icon: "Focus",
    title: "Be Present, Not Distracted",
    description: "Give your full attention to complex conversations and sensitive situations. No more splitting focus between your client and capturing notes - AI handles all the documentation."
  },
  {
    id: 2,
    icon: "FileText",
    title: "Smart Documentation",
    description: "AI automatically creates professional documents from your meetings - risk assessments, safeguarding referrals, ASB reports, support plans, tenancy reviews, and more."
  },
  {
    id: 3,
    icon: "Heart",
    title: "Better Client Outcomes",
    description: "When you're fully present, clients feel heard and valued. Build trust and rapport in difficult conversations - whether welfare visits, ASB interviews, or support sessions."
  },
  {
    id: 4,
    icon: "Shield",
    title: "GDPR Compliant",
    description: "All meeting data is automatically deleted after 3 days. Download your documents before expiry - your data security and client confidentiality is our priority."
  },
  {
    id: 5,
    icon: "Clock",
    title: "No More Writing Up Later",
    description: "Eliminate hours spent writing file notes after visits. No more trying to remember conversation details or manually logging communications on housing systems."
  },
  {
    id: 6,
    icon: "Users",
    title: "Built for Frontline Teams",
    description: "Perfect for support workers, neighbourhood officers, tenancy coaches, income officers, community safety teams, and anyone having complex client conversations."
  }
];

export const howItWorks = [
  {
    step: 1,
    title: "Start Recording",
    description: "Begin your home visit, office appointment, or phone call and tap to start. The app captures everything while you focus on your client."
  },
  {
    step: 2,
    title: "AI Transcription",
    description: "Our AI transcribes the conversation in real-time, capturing every important detail from complex discussions and sensitive disclosures."
  },
  {
    step: 3,
    title: "Smart Summary",
    description: "Get an intelligent summary highlighting key concerns, actions required, and important information - no more writing up notes later."
  },
  {
    step: 4,
    title: "Generate Documents",
    description: "Automatically populate your organisation's templates - support plans, risk assessments, ASB case notes, safeguarding referrals, and more."
  }
];

export const documentTypes = [
  "Risk Assessments",
  "Safeguarding Referrals",
  "ASB Case Notes",
  "Support Plans",
  "Tenancy Reviews",
  "Witness Statements",
  "Welfare Visit Reports",
  "Income Recovery Notes"
];

export const stats = [
  { value: "75%", label: "Time Saved on Documentation" },
  { value: "100%", label: "GDPR Compliant" },
  { value: "3 Days", label: "Auto Data Deletion" },
  { value: "24/7", label: "Always Available" }
];

export const pricingPlans = [
  {
    tier: "Free",
    price: "£0",
    billing: "One-time",
    credits: 150,
    time: "15 mins",
    meetings: "Trial",
    description: "Perfect for trying out Present Mind App",
    features: [
      "15 minutes of recording",
      "AI transcription",
      "Smart summaries",
      "1 document template",
      "3-day data retention"
    ],
    cta: "Start Free Trial",
    highlighted: false,
    badge: "Free Trial"
  },
  {
    tier: "Basic",
    price: "£4.99",
    billing: "Weekly",
    credits: 400,
    time: "40 mins",
    meetings: "~1",
    description: "Great for individual frontline staff",
    features: [
      "40 minutes of recording",
      "AI transcription",
      "Smart summaries",
      "All document templates",
      "3-day data retention",
      "Email support"
    ],
    cta: "Get Started",
    highlighted: false,
    badge: null
  },
  {
    tier: "Pro",
    price: "£9.99",
    billing: "Weekly",
    credits: 900,
    time: "90 mins",
    meetings: "~3",
    description: "Ideal for busy housing professionals",
    features: [
      "90 minutes of recording",
      "AI transcription",
      "Smart summaries",
      "All document templates",
      "3-day data retention",
      "Priority support",
      "Custom templates"
    ],
    cta: "Get Started",
    highlighted: true,
    badge: "Most Popular"
  },
  {
    tier: "Enterprise",
    price: "£19.99",
    billing: "Weekly",
    credits: 2500,
    time: "250 mins",
    meetings: "~8",
    description: "For high-volume users and teams",
    features: [
      "250 minutes of recording",
      "AI transcription",
      "Smart summaries",
      "All document templates",
      "3-day data retention",
      "Priority support",
      "Custom templates",
      "Dedicated account manager"
    ],
    cta: "Contact Us",
    highlighted: false,
    badge: "Best Value"
  }
];

export const complianceData = {
  headline: "Enterprise-Grade Security & Compliance",
  subheadline: "Built for housing organisations handling sensitive client data with the highest standards of protection",
  atAGlance: [
    { icon: "Globe", label: "Data Location", value: "United Kingdom Only" },
    { icon: "Lock", label: "Encryption", value: "AES-256 at rest, TLS 1.3 in transit" },
    { icon: "Clock", label: "Default Retention", value: "3 days for recordings" },
    { icon: "Shield", label: "Certification", value: "Cyber Essentials Plus" },
    { icon: "Server", label: "Hosting", value: "ISO 27001 Certified" },
    { icon: "Users", label: "Access Control", value: "Role-based (RBAC)" }
  ],
  documents: [
    { title: "GDPR Compliance Statement", updated: "December 2024" },
    { title: "Data Security Policy", updated: "December 2024" },
    { title: "Data Storage & Location", updated: "December 2024" },
    { title: "Data Retention Policy", updated: "December 2024" }
  ],
  categories: [
    "GDPR", "Security", "Data Storage", "Retention", "AI Transparency", "Safeguarding", "Accessibility", "Terms"
  ]
};

export const testimonials = [
  {
    id: 1,
    quote: "Present Mind App has transformed my home visits. I can finally give vulnerable tenants my full attention during difficult conversations without worrying about missing important details.",
    author: "Support Worker",
    organisation: "Housing Association",
    role: "Frontline Staff"
  },
  {
    id: 2,
    quote: "As an ASB officer, I deal with complex situations daily. The document generation means I'm not spending evenings writing up case notes - it's all done automatically.",
    author: "Community Safety Officer",
    organisation: "Local Authority Housing",
    role: "Specialist Team"
  },
  {
    id: 3,
    quote: "My income recovery calls are now properly documented without me having to type while talking. Clients notice the difference - they feel properly listened to.",
    author: "Income Officer",
    organisation: "Social Housing Provider",
    role: "Frontline Staff"
  }
];

export const faqData = [
  {
    question: "How does Present Mind App handle data privacy?",
    answer: "We take data privacy seriously. All meeting recordings and transcriptions are automatically deleted after 3 days in compliance with GDPR. You can download any required documents before the expiry countdown ends. This is essential when dealing with sensitive client information."
  },
  {
    question: "What types of documents can be generated?",
    answer: "Present Mind App can generate a wide range of housing documents including risk assessments, safeguarding referrals, ASB case notes, support plans, tenancy reviews, witness statements, welfare visit reports, income recovery notes, and more. Custom templates can be configured for your organisation."
  },
  {
    question: "Can I use it for phone calls as well as face-to-face meetings?",
    answer: "Yes! Present Mind App supports both face-to-face visits and phone calls. Whether you're conducting a home visit, office appointment, or telephone conversation, simply select the meeting type when you start recording."
  },
  {
    question: "How accurate is the AI transcription?",
    answer: "Our AI transcription achieves high accuracy rates and continuously improves. It's designed to handle various accents and conversation styles, and captures the nuance needed for complex housing situations and sensitive disclosures."
  },
  {
    question: "What roles is Present Mind App suitable for?",
    answer: "Present Mind App is designed for all frontline housing staff including support workers, neighbourhood officers, tenancy coaches, community safety officers, ASB officers, income officers, housing officers, and anyone who has client conversations involving complex situations or sensitive data."
  },
  {
    question: "How is Present Mind App licensed?",
    answer: "Present Mind App uses individual user accounts - each staff member has their own secure login. This ensures proper audit trails, data accountability, and personalised settings for every user. Organisation admins can easily manage user accounts and permissions from the admin dashboard."
  }
];

export const contactInfo = {
  email: "support@presentmindapp.co.uk",
  phone: "+44 (0) 123 456 7890",
  address: "United Kingdom"
};

export const logoUrl = "https://customer-assets.emergentagent.com/job_mindful-present/artifacts/4o3c4xk1_IMG_4032.jpeg";
